import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './ui/index.tsx';
import { api, ApiError } from '../lib/api.ts';
import { useSession } from '../state/session.tsx';
import './verify-banner.css';

/**
 * A standing reminder for an unconfirmed address.
 *
 * It is a nudge, not a wall: everything except issuing documents and inviting
 * people still works while it is showing, so a slow mail server costs the user
 * nothing but this strip.
 */
export function VerifyBanner() {
  const { user } = useSession();
  const [note, setNote] = useState<string | null>(null);

  const resend = useMutation({
    mutationFn: () => api.unscoped.post<{ sent: boolean }>('/api/auth/resend-verification'),
    onSuccess: () => setNote('Sent — check your inbox.'),
    onError: (error) =>
      setNote(error instanceof ApiError ? error.message : 'Could not send. Try again shortly.'),
  });

  if (!user || user.emailVerifiedAt) return null;

  return (
    <div className="verify-banner no-print" role="status">
      <span className="verify-banner__dot" aria-hidden="true" />
      <p className="verify-banner__text">
        {note ?? (
          <>
            Confirm <strong>{user.email}</strong> to issue documents and invite people.
          </>
        )}
      </p>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setNote(null);
          resend.mutate();
        }}
        loading={resend.isPending}
      >
        Resend
      </Button>
    </div>
  );
}
